import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  LinearProgress,
  Typography,
} from "@mui/material";
import React, {useEffect, useState} from "react";
import {productImageQRCodeRepository} from "../product-image-qrcode.repository";
import {useParams} from "react-router-dom";
import Swal from "sweetalert2";
import application from "../../core/application";
import {getDownloadURL, uploadBytesResumable} from "firebase/storage";
import {productRepository} from "../product.repository";
import {ProductImageQRCode} from "../product-image-qrcode.entity";
import {Product} from "../product.entity";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

export const ProductQrcodePage = () => {
  const {qrcodeId, token} = useParams<{qrcodeId: string; token: string}>();
  const [productImageQRCode, setProductImageQRCode] = useState<ProductImageQRCode>();
  const [product, setProduct] = useState<Product>();
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadingPercent, setImageUploadingPercent] = useState(0);
  const [fineshed] = useState(false);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    (async () => {
      if (!qrcodeId || !token) {
        setExpired(true);
        window.close();
        return;
      }

      const qrCodeImage = await productImageQRCodeRepository.findByIdOrNull(qrcodeId);

      if (!qrCodeImage) {
        setExpired(true);
        return;
      }

      const productResult = await productRepository.findByIdOrNull(qrCodeImage.productId);

      if (!productResult) {
        setExpired(true);
        return;
      }

      setProductImageQRCode(qrCodeImage);
      setProduct(productResult);
    })();
  }, [qrcodeId, token]);

  const onImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!productImageQRCode) return;
    if (!product) return;

    const fileInput = event.target;
    const file = (fileInput as HTMLInputElement)?.files?.[0];

    if (!file) {
      await Swal.fire({
        title: "Nenhuma imagem selecionada.",
        text: "Selecione uma imagem para continuar.",
        icon: "error",
      });

      return;
    }

    if (!file.type.startsWith("image/")) {
      Swal.fire({
        title: "Arquivo inválido.",
        text: "Selecione um arquivo de imagem.",
        icon: "error",
      });
      return;
    }

    setImageUploading(true);

    const ref = application.getStorageRef(`/products/${product.id}.${file.type.split("/")[1]}`);
    const uploadTask = uploadBytesResumable(ref, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setImageUploadingPercent(progress);
      },
      (error) => {
        console.error("Erro para upar imagem", error);
        Swal.fire({
          title: "Erro ao enviar imagem.",
          text: "Tente novamente.",
          icon: "error",
        });
        setImageUploading(false);
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then(async (downloadURL) => {
          if (!product) return;

          product.setImage(downloadURL);
          await productRepository.save(product);

          productImageQRCode.usaged = true;
          await productImageQRCodeRepository.save(productImageQRCode);

          Swal.fire({
            title: "Imagem enviada.",
            icon: "success",
          });
          window.close();
        });
      }
    );
  };

  if (!product || !productImageQRCode) {
    return (
      <Backdrop sx={{color: "#fff"}} open>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (imageUploading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          bgcolor: "background.default",
        }}
      >
        <Card sx={{width: "100%", maxWidth: 360}}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Enviando imagem…
            </Typography>
            <LinearProgress variant="determinate" value={imageUploadingPercent} sx={{mb: 1}} />
            <Typography variant="caption" color="text.secondary">
              {Math.round(imageUploadingPercent)}%
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (expired) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          bgcolor: "background.default",
        }}
      >
        <Card sx={{maxWidth: 400, textAlign: "center"}}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Link inválido ou expirado
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gere um novo QR Code na tela do produto.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (fineshed) {
    return (
      <Box sx={{p: 3, textAlign: "center"}}>
        <Typography variant="h6">Concluído</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        bgcolor: "background.default",
      }}
    >
      <Card sx={{width: "100%", maxWidth: 400}}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} gutterBottom>
            Foto do produto
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
            Escolha uma imagem nítida; ela será salva no cadastro do produto.
          </Typography>
          <input
            type="file"
            id="image"
            accept="image/*"
            style={{display: "none"}}
            onChange={onImageChange}
          />
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<CloudUploadIcon />}
            onClick={() => document.getElementById("image")?.click()}
          >
            Escolher imagem
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};
